import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { OrgFeatureFlagKey } from '../utils/orgFeatureFlagKeys';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import type { DescriptionSection, DescriptionTemplate, IntegrationSettings } from '../services/integrationApi';
import { InlineMessage, SectionCard } from './ui';

export interface ShopifyDescriptionTemplateSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  /** Absent while an integration is being created: there is nothing to preview against yet. */
  integrationId?: string;
  /** The flag that shows the section; Shopify's `description_templates` unless a platform names its own. */
  flagKey?: OrgFeatureFlagKey;
  /** Lightspeed writes plain HTML (no accordion) and has no preview route: hide both controls. */
  plainHtml?: boolean;
  /** Overrides the Shopify subtitle copy. */
  subtitleKey?: string;
}

const T = 'integrations.descriptionTemplate';

export const ALL_DESCRIPTION_SECTIONS: DescriptionSection[] = ['prose', 'features', 'specs', 'compatibility'];

/** What a store that never chose gets, written as a template. */
export const DEFAULT_DESCRIPTION_TEMPLATE: DescriptionTemplate = {
  sections: ['prose', 'features', 'specs'],
  prose: 'full',
  accordion: true,
};

/** Moves the section at `index` by `delta` places (-1 up, 1 down); out-of-range moves return the list unchanged. */
export function moveSection(sections: DescriptionSection[], index: number, delta: -1 | 1): DescriptionSection[] {
  const target = index + delta;
  if (index < 0 || index >= sections.length || target < 0 || target >= sections.length) return sections;
  const next = [...sections];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

interface PreviewResult {
  productId: string;
  productName: string;
  html: string;
}

/**
 * Description layout (flag `description_templates`, Estimate row B16): which sections of the synced
 * Shopify description appear, in what order, short or full prose, and accordion or open sections.
 * Hidden with the flag off. Saved with the rest of the integration settings.
 */
const ShopifyDescriptionTemplateSection: React.FC<ShopifyDescriptionTemplateSectionProps> = ({
  settings,
  onChange,
  integrationId,
  flagKey = 'description_templates',
  plainHtml = false,
  subtitleKey,
}) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag(flagKey);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const saved = settings.descriptionTemplate || null;
  const template = saved || DEFAULT_DESCRIPTION_TEMPLATE;

  const previewMutation = useMutation<PreviewResult, Error, DescriptionTemplate>({
    mutationFn: async (body) => {
      const response = await makeApiCall(`/integrations/${integrationId}/description-preview`, {
        method: 'POST',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ template: body }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.message || json?.error || t(`${T}.previewFailed`));
      return json.data as PreviewResult;
    },
    onSuccess: (data) => setPreview(data),
  });

  if (!enabled) return null;

  const update = (partial: Partial<DescriptionTemplate>) => {
    setPreview(null);
    onChange({ descriptionTemplate: { ...template, ...partial } });
  };
  const excluded = ALL_DESCRIPTION_SECTIONS.filter((s) => !template.sections.includes(s));

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(subtitleKey || `${T}.subtitle`)} dense padded>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t(`${T}.sections.label`)}
          </Typography>
          <Stack spacing={0.5} component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {template.sections.map((section, index) => (
              <Box
                component="li"
                key={section}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.25 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t(`${T}.sections.${section}`)}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t(`${T}.sections.moveUp`, { section: t(`${T}.sections.${section}`) })}
                  disabled={index === 0}
                  onClick={() => update({ sections: moveSection(template.sections, index, -1) })}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t(`${T}.sections.moveDown`, { section: t(`${T}.sections.${section}`) })}
                  disabled={index === template.sections.length - 1}
                  onClick={() => update({ sections: moveSection(template.sections, index, 1) })}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t(`${T}.sections.remove`, { section: t(`${T}.sections.${section}`) })}
                  onClick={() => update({ sections: template.sections.filter((s) => s !== section) })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
          {excluded.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {excluded.map((section) => (
                <Button
                  key={section}
                  size="small"
                  variant="outlined"
                  onClick={() => update({ sections: [...template.sections, section] })}
                >
                  {t(`${T}.sections.add`, { section: t(`${T}.sections.${section}`) })}
                </Button>
              ))}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {t(plainHtml ? `${T}.lightspeedSectionsDesc` : `${T}.sections.desc`)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }} id="description-template-prose">
            {t(`${T}.prose.label`)}
          </Typography>
          <RadioGroup
            row
            aria-labelledby="description-template-prose"
            value={template.prose}
            onChange={(e) => update({ prose: e.target.value as 'full' | 'short' })}
          >
            <FormControlLabel value="full" control={<Radio size="small" />} label={t(`${T}.prose.full`)} />
            <FormControlLabel value="short" control={<Radio size="small" />} label={t(`${T}.prose.short`)} />
          </RadioGroup>
        </Box>

        {!plainHtml && (
        <Box>
          <FormControlLabel
            control={<Switch checked={template.accordion} onChange={(e) => update({ accordion: e.target.checked })} />}
            label={t(`${T}.accordion.label`)}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {t(`${T}.accordion.desc`)}
          </Typography>
        </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          {!plainHtml && (
            <Button
              size="small"
              variant="outlined"
              disabled={!integrationId || previewMutation.isPending}
              onClick={() => previewMutation.mutate(template)}
            >
              {t(`${T}.preview`)}
            </Button>
          )}
          {previewMutation.isPending && <CircularProgress size={18} />}
          {saved && (
            <Button
              size="small"
              onClick={() => {
                setPreview(null);
                onChange({ descriptionTemplate: null });
              }}
            >
              {t(`${T}.reset`)}
            </Button>
          )}
          {!saved && (
            <Typography variant="caption" color="text.secondary">
              {t(`${T}.notCustomised`)}
            </Typography>
          )}
        </Stack>

        {previewMutation.isError && <InlineMessage severity="error">{previewMutation.error.message}</InlineMessage>}
        {preview && (
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              {t(`${T}.previewOf`, { name: preview.productName })}
            </Typography>
            <Box
              component="iframe"
              title={t(`${T}.previewTitle`)}
              sandbox=""
              srcDoc={preview.html}
              sx={{ width: '100%', minHeight: 320, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
            />
          </Box>
        )}
      </Stack>
    </SectionCard>
  );
};

export default ShopifyDescriptionTemplateSection;
